import type { GallerySnapshot, MetricSeries, RunProjection } from "@roboscientist/contracts";
import { isRunningRun, runEnd, runStart } from "../../web/src/components/runTiming.js";
import { findTimelineBreaks } from "../../web/src/components/timelineBreaks.js";

type RunSnapshot = Pick<GallerySnapshot, "snapshot" | "runs" | "experiments" | "metrics">;
type RunInterval = { run: RunProjection; start: number; end: number };
export type GalleryRunReplayPlan = {
  duration: number;
  observedAt: number;
  start: number;
  end: number;
  intervals: RunInterval[];
  segments: Array<{ start: number; end: number; from: number; to: number }>;
};
const finite = (value: number, fallback: number) => Number.isFinite(value) ? value : fallback;

const referencesRun = (references: string[], run: RunProjection) => references.some((reference) => [run.id, run.declaredRunId, run.path].some((id) => id && reference.includes(id)));

export function scopeGalleryRunReplaySnapshot(snapshot: RunSnapshot, runIds: ReadonlySet<string>): RunSnapshot {
  const runs = snapshot.runs.filter((run) => runIds.has(run.id));
  if (runs.length === snapshot.runs.length) return snapshot;
  return { ...snapshot, runs, metrics: snapshot.metrics.filter((entry) => runIds.has(entry.runId)),
    experiments: snapshot.experiments.map((experiment) => {
      const owned = snapshot.runs.filter((run) => run.experimentId === experiment.id);
      const progress = experiment.progress;
      return { ...experiment, runCount: owned.filter((run) => runIds.has(run.id)).length,
        progress: progress ? { ...progress, progress: progress.progress.filter((entry) => {
          const linked = owned.filter((run) => referencesRun(entry.references, run));
          return !linked.length || linked.some((run) => runIds.has(run.id));
        }) } : null };
    })
  };
}

export function buildGalleryRunReplayPlan(snapshot: RunSnapshot): GalleryRunReplayPlan {
  const observedAt = finite(Date.parse(snapshot.snapshot.exportedAt), Math.max(0, ...snapshot.runs.map((run) => finite(Date.parse(run.updatedAt), 0))));
  const intervals = snapshot.runs.map((run) => {
    const start = finite(runStart(run), observedAt);
    return { run, start, end: Math.max(start + 1_000, finite(runEnd(run, observedAt), start + 1_000)) };
  }).sort((a, b) => a.start - b.start || a.run.id.localeCompare(b.run.id));
  const times = [...new Set(intervals.flatMap(({ start, end }) => [start, end]))].sort((a, b) => a - b);
  const gaps = findTimelineBreaks({ intervals, domainStart: times[0] ?? observedAt, domainEnd: times.at(-1) ?? observedAt, thresholdRatio: 0 });
  // Idle gaps cross in 100 ms; reserve the replay budget for recorded activity.
  const weights = times.slice(1).map((end, i) => gaps.some((gap) => gap.start <= times[i]! && gap.end >= end) ? 0 : Math.sqrt(end - times[i]!));
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  let elapsed = 600;
  // Compress recorded time while giving short attempts a visible part in the replay.
  const segments = times.slice(1).map((end, i) => {
    const from = elapsed;
    elapsed += 100 + weights[i]! / totalWeight * 24_000;
    return { start: times[i]!, end, from, to: elapsed };
  });
  return { observedAt, intervals, segments, start: times[0] ?? observedAt, end: times.at(-1) ?? observedAt, duration: intervals.length ? Math.round(elapsed + 400) : 0 };
}

export function projectGalleryRunMetrics(series: MetricSeries[], interval: Pick<RunInterval, "start" | "end">, cursorTime: number): MetricSeries[] {
  if (cursorTime >= interval.end) return series;
  const ratio = Math.max(0, (cursorTime - interval.start) / (interval.end - interval.start));
  return series.map((entry) => {
    const maximumStep = entry.points.reduce((maximum, point) => Math.max(maximum, point.step), 1);
    return { ...entry, points: entry.points.filter((point) => {
      const recordedAt = point.wallTime ? Date.parse(point.wallTime) : NaN;
      return Number.isFinite(recordedAt) && recordedAt >= interval.start && recordedAt <= interval.end
        ? recordedAt <= cursorTime
        : cursorTime >= interval.start && point.step / maximumStep <= ratio;
    }) };
  });
}

/** The replay camera samples nearby clock positions; saved evidence still uses the exact cursor. */
export function galleryRunCursor(plan: GalleryRunReplayPlan, elapsed: number): number {
  const segment = plan.segments.find((entry) => elapsed <= entry.to);
  return elapsed >= plan.duration || !segment ? plan.end : elapsed < 600 ? plan.start - 1
    : segment.start + Math.max(0, (elapsed - segment.from) / (segment.to - segment.from)) * (segment.end - segment.start);
}

export function galleryRunCameraTimes(plan: GalleryRunReplayPlan, elapsed: number): number[] {
  const radius = plan.duration * .014;
  return Array.from({ length: 13 }, (_, index) => galleryRunCursor(plan,
    Math.max(0, Math.min(plan.duration, elapsed + (index - 6) / 6 * radius))));
}

export function projectGalleryRunReplay(snapshot: RunSnapshot, plan: GalleryRunReplayPlan, elapsed: number) {
  const complete = elapsed >= plan.duration;
  const cursorTime = galleryRunCursor(plan, elapsed);
  const visibleRunIds = new Set<string>();
  const activeRunIds = new Set<string>();
  const runDetails = new Map<string, RunProjection>();
  const metricByRun = new Map(snapshot.metrics.map((entry) => [entry.runId, entry.series]));
  const metrics = new Map<string, MetricSeries[]>();
  for (const interval of plan.intervals) {
    const { run, start, end } = interval;
    if (!complete && start > cursorTime) continue;
    visibleRunIds.add(run.id);
    const active = !complete && cursorTime < end;
    if (active) activeRunIds.add(run.id);
    const series = projectGalleryRunMetrics(metricByRun.get(run.id) ?? [], interval, cursorTime);
    metrics.set(run.id, series);
    if (active) {
      const primary = series.find((entry) => entry.name === run.primaryMetricName)?.points.at(-1);
      const latestStep = series.reduce<number | null>((maximum, entry) => entry.points.reduce<number | null>(
        (max, point) => Math.max(max ?? 0, point.step), maximum
      ), null);
      runDetails.set(run.id, {
        ...run,
        currentStep: latestStep,
        progress: { ...run.progress, current: /^steps?$/.test(run.progress.unit) ? latestStep : null, stage: null, stageCurrent: null },
        primaryMetricValue: primary?.value ?? null,
        etaSeconds: null
      });
    } else {
      // Completed Runs already contain the saved values; do not rescan all their points.
      runDetails.set(run.id, run);
    }
  }
  const revealed = plan.intervals.filter(({ run }) => visibleRunIds.has(run.id));
  const withMetrics = (entries: RunInterval[]) => entries.filter(({ run }) => metrics.get(run.id)?.some((entry) => entry.points.length));
  const active = revealed.filter(({ run }) => activeRunIds.has(run.id));
  const focusRunId = (withMetrics(active).at(-1) ?? withMetrics(revealed).at(-1) ?? active.at(-1) ?? revealed.at(-1))?.run.id ?? null;
  const experiments = complete ? snapshot.experiments : snapshot.experiments.map((experiment) => {
    const owned = plan.intervals.filter(({ run }) => run.experimentId === experiment.id);
    const progress = experiment.progress;
    return { ...experiment, runCount: owned.filter(({ run }) => visibleRunIds.has(run.id)).length, progress: progress ? { ...progress,
      progress: progress.progress.filter((entry) => {
        const linked = owned.filter(({ run }) => referencesRun(entry.references, run));
        const revealedAt = linked.length ? Math.max(...linked.map(({ end }) => end)) : finite(Date.parse(entry.updatedAt), plan.end);
        return revealedAt <= cursorTime;
      })
    } : null };
  });
  return {
    complete, cursorTime, visibleRunIds, activeRunIds, runDetails, metrics, experiments, focusRunId,
    runs: complete ? snapshot.runs : snapshot.runs.filter((run) => visibleRunIds.has(run.id)).map((run) => runDetails.get(run.id)!),
    finishedCount: revealed.filter(({ end }) => end <= cursorTime).length,
    savedActiveCount: snapshot.runs.filter(isRunningRun).length
  };
}
